import React from 'react';
import dayjs from 'dayjs';
import { Card, Badge, ProgressBar } from 'react-bootstrap';
import { KanbanCard } from '../../../DesignSystems/Kanban';
import { ResourceRequest } from '../../hooks/incidentResourceRequestHooks';
import { ResourceSite } from '../../hooks/resourceSiteHooks';
import { ResourceRequestMetadata } from '../types/KanbanTypes';
import NeedCard from './KanbanCards/NeedCard';
import RequestDetails from './KanbanCards/RequestDetails';
import ApprovedDetails from './KanbanCards/ApprovedDetails';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';

interface ResourceRequestKanbanCardProps {
  card: KanbanCard;
  onCreateRequest?: (metadata: any) => void;
  onViewEditRequest?: (metadata: any) => void;
  onRestoreRequest?: (metadata: any) => void;
  onViewDetails?: (metadata: any) => void;
  resourceRequests?: ResourceRequest[];
  resourceSites?: ResourceSite[];
  isRestoring?: boolean;
}

const ResourceRequestKanbanCard: React.FC<ResourceRequestKanbanCardProps> = ({
  card,
  onCreateRequest,
  onViewEditRequest,
  onRestoreRequest,
  onViewDetails,
  resourceRequests,
  resourceSites,
  isRestoring = false,
}) => {
  const { exportAsForm } = useIcsFormPdfPreview();
  const metadata = card.metadata as ResourceRequestMetadata;
  if (!metadata) return null;

  const getFooterText = () => {
    switch (metadata.cardType) {
      case 'need':
        return metadata.requirements
          ? `${metadata.requirements.length} requirement${metadata.requirements.length > 1 ? 's' : ''}`
          : '0 requirements';
      case 'request':
        return metadata.items && metadata.items.length > 0
          ? `Total Qty: ${metadata.items.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0)}`
          : null;
      case 'approved':
      case 'cancelled':
        return (
          metadata.timestamp &&
          dayjs(metadata.timestamp).format('MM/DD/YYYY HH:mm')
        );
      default:
        return null;
    }
  };

  const handleDownload213RR = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (metadata.resource_request_id) {
      void exportAsForm({
        formname: 'ICS_213RR_CG',
        data: { requestId: metadata.resource_request_id },
        mapSketch: null,
      });
    } else {
      console.warn('No resource_request_id found in metadata', {
        metadata,
        allKeys: Object.keys(metadata),
      });
    }
  };

  const renderFooterButton = () => {
    switch (metadata.cardType) {
      case 'need':
        return (
          <StylishNewButton
            className="btn btn-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onCreateRequest?.(metadata);
            }}
          >
            Create Request
          </StylishNewButton>
        );
      case 'cancelled':
        return (
          <StylishNewButton
            className="btn btn-warning btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onRestoreRequest?.(metadata);
            }}
            disabled={isRestoring}
          >
            Restore
          </StylishNewButton>
        );
      case 'request':
      case 'approved':
        // For resource requests, show both View Details and Download 213RR
        return (
          <div className="d-flex gap-1">
            <StylishNewButton
              className="btn btn-outline-secondary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                onViewEditRequest?.(metadata);
              }}
            >
              View Details
            </StylishNewButton>
            <StylishNewButton
              className="btn btn-outline-primary btn-sm"
              onClick={handleDownload213RR}
              title="Preview 213RR"
            >
              Preview 213RR
            </StylishNewButton>
          </div>
        );
      default:
        return (
          <StylishNewButton
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(metadata);
            }}
          >
            View Details
          </StylishNewButton>
        );
    }
  };

  const handleCardClick = () => {
    if (metadata.cardType === 'need') {
      onCreateRequest?.(metadata);
    } else if (
      metadata.cardType === 'request' ||
      metadata.cardType === 'approved'
    ) {
      onViewEditRequest?.(metadata);
    } else {
      onViewDetails?.(metadata);
    }
  };

  return (
    <Card
      className="mb-2 border bg-white"
      style={{
        cursor: 'pointer',
        borderLeft: `4px solid ${
          metadata.cardType === 'need'
            ? '#dc3545'
            : metadata.cardType === 'request'
              ? '#ffc107'
              : metadata.cardType === 'cancelled'
                ? '#6c757d'
                : '#28a745'
        }`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      }}
      onClick={handleCardClick}
    >
      <Card.Body className="p-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1 text-dark">{card.title}</h6>
            {metadata.order_number && (
              <Badge
                bg="secondary"
                className="me-2"
                style={{ fontSize: '0.7rem' }}
              >
                {metadata.order_number}
              </Badge>
            )}
          </div>
          {/* Show priority from first item for request cards - only in header */}
          {(metadata.cardType === 'request' ||
            metadata.cardType === 'approved') &&
            metadata.items?.[0]?.priority === 'Urgent' && (
              <Badge bg="danger" style={{ fontSize: '0.7rem' }}>
                URGENT
              </Badge>
            )}
        </div>

        {/* Details */}
        <div className="small text-muted">
          {card.description}
          {metadata.cardType === 'need' && (
            <NeedCard metadata={metadata} resourceRequests={resourceRequests} />
          )}
          {metadata.cardType === 'request' && (
            <RequestDetails metadata={metadata} resourceSites={resourceSites} />
          )}
          {metadata.cardType === 'approved' && (
            <ApprovedDetails
              metadata={metadata}
              resourceSites={resourceSites}
            />
          )}
          {metadata.logistics_order_number && (
            <div className="mt-1">
              <strong>Order #:</strong> {metadata.logistics_order_number}
            </div>
          )}
        </div>

        {/* Progress for requests */}
        {metadata.approval && (
          <div className="mt-2">
            <div className="text-muted small">
              {metadata.approval.currentStep}
            </div>
            <ProgressBar
              now={metadata.approval.progress}
              style={{ height: '6px' }}
              variant={
                metadata.approval.progress === 100 ? 'success' : 'warning'
              }
            />
          </div>
        )}

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          <small className="text-muted">{getFooterText()}</small>
          {renderFooterButton()}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ResourceRequestKanbanCard;
